import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Stack,
} from '@mui/material'
import { Save as SaveIcon, Refresh as RefreshIcon, Preview as PreviewIcon } from '@mui/icons-material'
import DigestEmailPreview from '../components/DigestEmailPreview'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import { settingsApi, usersApi, Setting, User } from '../services/api'
import { useNotification } from '../hooks/useNotification'
import { userAtom } from '../state/auth'
import { isAdminOrManager } from '../utils/roles'
import {
  ASSIGNMENT_OVERDUE_SETTING_KEY,
  DEFAULT_ASSIGNMENT_OVERDUE_DAYS,
} from '../utils/contactAssignment'

// Setting definitions with labels and descriptions
const SETTING_DEFINITIONS: Record<string, { label: string; description: string; type: 'user-select' | 'text' }> = {
  defaultLeadOwner: {
    label: 'Default Lead Owner',
    description: 'The default owner assigned to new leads. Users can override this when creating a lead.',
    type: 'user-select',
  },
  defaultClientSuccessRep: {
    label: 'Default Client Success Rep',
    description: 'The default client success rep assigned to new clients. Can be overridden when assigning a team.',
    type: 'user-select',
  },
  defaultResumeWriter: {
    label: 'Default Resume Writer',
    description: 'The default resume writer assigned to new clients. Can be overridden when assigning a team.',
    type: 'user-select',
  },
  defaultCoach: {
    label: 'Default Coach',
    description: 'The default coach assigned to new clients. Can be overridden when assigning a team.',
    type: 'user-select',
  },
  defaultSalesRep: {
    label: 'Default Sales Rep',
    description: 'The default sales rep for lead qualification. Can be overridden when creating a lead.',
    type: 'user-select',
  },
}

type DigestSettingType = 'number' | 'toggle' | 'textarea'

const DIGEST_COMPACT_LEFT: { key: string; label: string; description: string; type: DigestSettingType }[] = [
  { key: 'digest_weekly_goal', label: 'Weekly Side Door Goal', description: 'Number of touchpoints clients should aim for per week', type: 'number' },
  { key: 'digest_idle_threshold', label: 'Idle Threshold (days)', description: 'Days of no activity before flagging a contact as stale', type: 'number' },
]

const DIGEST_COMPACT_RIGHT: { key: string; label: string; description: string; type: DigestSettingType }[] = [
  { key: 'digest_nudge_enabled', label: 'Weekly Nudge', description: 'Send weekly nudge email to clients below their goal', type: 'toggle' },
  { key: 'digest_daily_enabled', label: 'Daily In-App Digest', description: 'Show daily stale contact digest in the client portal', type: 'toggle' },
  { key: 'digest_staff_summary_enabled', label: 'Staff Weekly Summary', description: 'Generate weekly client health summary for coaches', type: 'toggle' },
]

const DIGEST_SETTINGS: { key: string; label: string; description: string; type: DigestSettingType }[] = [
  ...DIGEST_COMPACT_LEFT,
  ...DIGEST_COMPACT_RIGHT,
  { key: 'digest_nudge_message', label: 'Nudge Message', description: 'Merge fields: {count}, {goal}', type: 'textarea' },
  { key: 'digest_congrats_message', label: 'Congrats Message', description: 'Merge fields: {count}, {goal}', type: 'textarea' },
  { key: 'digest_idle_message', label: 'Idle Contact Message', description: 'Merge fields: {contactName}, {days}', type: 'textarea' },
  { key: 'digest_outreach_followup_message', label: 'Outreach Follow-Up Message', description: 'Merge fields: {contactName}, {days}', type: 'textarea' },
]

export default function Settings() {
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<Setting[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const isAdmin = isAdminOrManager(user)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch users first (this should always work)
      const usersData = await usersApi.list({ status: 'active' })
      const eligibleUsers = (usersData || []).filter((u) =>
        ['admin', 'manager', 'sales', 'client_success', 'resume_writer'].includes(u.role)
      )
      setUsers(eligibleUsers)

      // Try to fetch settings (may fail if Lambda/table not deployed yet)
      let settingsData: Setting[] = []
      try {
        settingsData = await settingsApi.list()
      } catch (settingsErr) {
        console.warn('Settings API not available yet:', settingsErr)
        // Use empty settings - user can still configure once API is deployed
      }

      setSettings(Array.isArray(settingsData) ? settingsData : [])

      // Initialize edited values with current settings
      const initialValues: Record<string, string> = {}
      // First, set defaults for all defined settings
      for (const key of Object.keys(SETTING_DEFINITIONS)) {
        initialValues[key] = ''
      }
      // Set defaults for digest settings
      for (const def of DIGEST_SETTINGS) {
        initialValues[def.key] = ''
      }
      initialValues[ASSIGNMENT_OVERDUE_SETTING_KEY] = ''
      // Then override with actual values from API
      if (Array.isArray(settingsData)) {
        for (const setting of settingsData) {
          if (setting?.key) {
            initialValues[setting.key] = setting.value || ''
          }
        }
      }
      setEditedValues(initialValues)
    } catch (err) {
      console.error('Error loading settings:', err)
      setError('Failed to load settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async (key: string) => {
    try {
      setSaving(true)
      await settingsApi.update(key, editedValues[key])
      showSuccess('Setting saved successfully')
      loadData()
    } catch (err) {
      console.error('Error saving setting:', err)
      showError('Failed to save setting')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = (key: string) => {
    if (!Array.isArray(settings)) return true
    const currentSetting = settings.find((s) => s.key === key)
    const currentValue = currentSetting?.value || ''
    return editedValues[key] !== currentValue
  }

  // MWA-354 — a whole number of days, at least 1. Blank means "use the
  // default", which is also what an unsaved key reads as, so blank is not saved.
  const overdueDaysValue = editedValues[ASSIGNMENT_OVERDUE_SETTING_KEY] || ''
  const overdueDaysInvalid = overdueDaysValue !== '' && !/^[1-9]\d*$/.test(overdueDaysValue.trim())
  const overdueDaysSetting = Array.isArray(settings)
    ? settings.find((s) => s.key === ASSIGNMENT_OVERDUE_SETTING_KEY)
    : undefined

  if (loading) {
    return <LoadingSpinner />
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Settings</Typography>
          <PageHelpButton articleId="admin-settings-configure" />
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Default Assignments
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {Object.entries(SETTING_DEFINITIONS).map(([key, def]) => {
            const currentValue = editedValues[key] || ''
            const changed = hasChanges(key)

            return (
              <Box key={key} sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {def.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {def.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  {def.type === 'user-select' ? (
                    <FormControl sx={{ minWidth: 300 }}>
                      <InputLabel>Select User</InputLabel>
                      <Select
                        value={currentValue}
                        label="Select User"
                        onChange={(e) => handleChange(key, e.target.value)}
                      >
                        <MenuItem value="">
                          <em>None (no default)</em>
                        </MenuItem>
                        {users.map((u) => (
                          <MenuItem key={u.email} value={u.email}>
                            {u.name} ({u.role})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      value={currentValue}
                      onChange={(e) => handleChange(key, e.target.value)}
                      sx={{ minWidth: 300 }}
                    />
                  )}

                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={() => handleSave(key)}
                    disabled={!changed || saving}
                  >
                    Save
                  </Button>
                </Box>

                {Array.isArray(settings) && settings.find((s) => s.key === key)?.updatedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Last updated: {new Date(settings.find((s) => s.key === key)!.updatedAt!).toLocaleString()}
                    {settings.find((s) => s.key === key)?.updatedBy &&
                      ` by ${settings.find((s) => s.key === key)!.updatedBy}`}
                  </Typography>
                )}
              </Box>
            )
          })}
        </CardContent>
      </Card>

      {/* Smart Digest & Prompts */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Smart Digest & Prompts
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewOpen(true)}
            >
              Preview Email
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Configure automated client nudges, digest notifications, and staff summaries.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Compact settings — two-column layout */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Left column: number inputs */}
            <Grid size={{ xs: 12, md: 6 }}>
              {DIGEST_COMPACT_LEFT.map((def) => {
                const currentValue = editedValues[def.key] || ''
                const changed = hasChanges(def.key)
                return (
                  <Box key={def.key} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {def.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {def.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <TextField
                        value={currentValue}
                        onChange={(e) => handleChange(def.key, e.target.value)}
                        type="number"
                        inputProps={{ min: 1 }}
                        sx={{ width: 150 }}
                      />
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={() => handleSave(def.key)}
                        disabled={!changed || saving}
                      >
                        Save
                      </Button>
                    </Box>
                    {Array.isArray(settings) && settings.find((s) => s.key === def.key)?.updatedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Last updated: {new Date(settings.find((s) => s.key === def.key)!.updatedAt!).toLocaleString()}
                        {settings.find((s) => s.key === def.key)?.updatedBy &&
                          ` by ${settings.find((s) => s.key === def.key)!.updatedBy}`}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Grid>
            {/* Right column: toggles */}
            <Grid size={{ xs: 12, md: 6 }}>
              {DIGEST_COMPACT_RIGHT.map((def) => {
                const currentValue = editedValues[def.key] || ''
                const changed = hasChanges(def.key)
                return (
                  <Box key={def.key} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {def.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {def.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentValue === 'true'}
                            onChange={(e) => handleChange(def.key, e.target.checked ? 'true' : 'false')}
                          />
                        }
                        label={currentValue === 'true' ? 'Enabled' : 'Disabled'}
                      />
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={() => handleSave(def.key)}
                        disabled={!changed || saving}
                      >
                        Save
                      </Button>
                    </Box>
                    {Array.isArray(settings) && settings.find((s) => s.key === def.key)?.updatedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Last updated: {new Date(settings.find((s) => s.key === def.key)!.updatedAt!).toLocaleString()}
                        {settings.find((s) => s.key === def.key)?.updatedBy &&
                          ` by ${settings.find((s) => s.key === def.key)!.updatedBy}`}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Message template settings — full width */}
          {DIGEST_SETTINGS.filter((d) => d.type === 'textarea').map((def) => {
            const currentValue = editedValues[def.key] || ''
            const changed = hasChanges(def.key)

            return (
              <Box key={def.key} sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {def.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {def.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    value={currentValue}
                    onChange={(e) => handleChange(def.key, e.target.value)}
                    multiline
                    rows={3}
                    sx={{ minWidth: 400, flex: 1 }}
                    placeholder={def.description}
                  />

                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={() => handleSave(def.key)}
                    disabled={!changed || saving}
                  >
                    Save
                  </Button>
                </Box>

                {Array.isArray(settings) && settings.find((s) => s.key === def.key)?.updatedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Last updated: {new Date(settings.find((s) => s.key === def.key)!.updatedAt!).toLocaleString()}
                    {settings.find((s) => s.key === def.key)?.updatedBy &&
                      ` by ${settings.find((s) => s.key === def.key)!.updatedBy}`}
                  </Typography>
                )}
              </Box>
            )
          })}
        </CardContent>
      </Card>

      {/* Networking accountability (MWA-354) */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Side Door accountability
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            How coaches see the contacts they have assigned to a client on the Side Door page.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" fontWeight="medium">
            Assignment overdue after (days)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            An assigned contact the client has not reached out to is flagged overdue once it has
            waited longer than this. Leave blank to use the default of {DEFAULT_ASSIGNMENT_OVERDUE_DAYS}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              value={overdueDaysValue}
              onChange={(e) => handleChange(ASSIGNMENT_OVERDUE_SETTING_KEY, e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1, 'aria-label': 'Assignment overdue after (days)' }}
              placeholder={String(DEFAULT_ASSIGNMENT_OVERDUE_DAYS)}
              error={overdueDaysInvalid}
              helperText={overdueDaysInvalid ? 'Enter a whole number of days, 1 or more' : ' '}
              sx={{ width: 150 }}
            />
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => handleSave(ASSIGNMENT_OVERDUE_SETTING_KEY)}
              disabled={
                !hasChanges(ASSIGNMENT_OVERDUE_SETTING_KEY) ||
                overdueDaysInvalid ||
                overdueDaysValue.trim() === '' ||
                saving
              }
            >
              Save
            </Button>
          </Box>
          {overdueDaysSetting?.updatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Last updated: {new Date(overdueDaysSetting.updatedAt).toLocaleString()}
              {overdueDaysSetting.updatedBy && ` by ${overdueDaysSetting.updatedBy}`}
            </Typography>
          )}
        </CardContent>
      </Card>

      <DigestEmailPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        nudgeMessage={editedValues['digest_nudge_message'] || ''}
        congratsMessage={editedValues['digest_congrats_message'] || ''}
        idleMessage={editedValues['digest_idle_message'] || ''}
        weeklyGoal={editedValues['digest_weekly_goal'] || ''}
      />
    </Box>
  )
}
